import React from 'react';
import styled from 'styled-components';
import { columnTypes, DataTable, extraStrikesPer100, LoadingSpinner, percent, pitchTypeOrSubType, ratio, slg } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { rvPer100BIP } from '../../../../../utils/stats';
import StatTooltip from '../../../../../components/StatTooltip';

const SuperHeaderRow = styled.tr`
    background-color: #C6D3DC;
    td {
        border-left: 1px solid #FFF;
        border-right: 1px solid #FFF;
        background-color: #244C74 !important;
        color: #FFF;
        text-align: center;
        font-weight: 600;
    }
`;

const pitchColumnDisplay = (r, isLhp) => {
    if (r.pitchtype === 1) return 'All FB';
    if (r.pitchtype === 0) return 'Non-FB';
    if (r.pitchtype === -1) return 'Total';
    if (r.pitchtype === -2) return `${isLhp ? 'LHP' : 'RHP'} MLB Avg`;
    return pitchTypeOrSubType(r.fxsubtype || r.pitchtype, true);
};

const PitchTabTable = ({ pitchTypes, isLhp, isLoadingAvg }) => {
    const { t: translate } = useTranslation();

    const SuperHeader = (
        <SuperHeaderRow>
            <td colSpan={4}>{translate('sections.reports.pitcher.pitchcomparison.general')}</td>
            <td colSpan={3}>{translate('sections.reports.pitcher.pitchcomparison.results')}</td>
            <td colSpan={4}>{translate('sections.reports.pitcher.pitchcomparison.pitch-quality')}</td>
            <td colSpan={7}>{translate('sections.reports.pitcher.pitchcomparison.movement')}</td>
            <td colSpan={5}>{translate('sections.reports.pitcher.pitchcomparison.controllingthezone')}</td>
            <td colSpan={2}>{translate('sections.reports.pitcher.pitchcomparison.missingbats')}</td>
            <td colSpan={4}>{translate('sections.reports.pitcher.pitchcomparison.batted-balls')}</td>
        </SuperHeaderRow>
    );

    const columns = [
        {
            id: 'pitch',
            title: translate('baseball.pitches_one'),
            type: columnTypes.NODE,
            transform: (r) => pitchColumnDisplay(r, isLhp),
            cellClass: 'fw-bold',
            colSpan: 1,
        },
        {
            id: 'loading',
            colSpan: (r) => (r?.isLoadingRow && isLoadingAvg ? 99 : 0),
            type: columnTypes.NODE,
            transform: () => <LoadingSpinner size="sm" />,
        },
        {
            id: 'pitches',
            title: translate('sections.reports.pitcher.pitchcomparison.numpitches'),
            statistic: 'Pitches',
            type: columnTypes.NODE,
            transform: (r) => (r.pitchtype === -2 ? '--' : r.pitches),
        },
        {
            id: 'pitperc',
            title: translate('pitcher-overview.pitch-break.pit-percent'),
            statistic: 'PitchTypePercent',
            type: columnTypes.NODE,
            transform: (r) => (r.pitchtype < 0 ? '--' : percent(ratio(r.pitches, r.totalpitches))),
        },
        {
            id: 'velocity',
            title: translate('sections.reports.pitcher.abbr-velo'),
            statistic: 'AvgVelo',
            headerClass: 'border-end',
            cellClass: 'border-end',
            precision: 1,
            transform: (r) => (r.pitchtype < 0 ? '--' : r.velocity),
        },
        {
            id: 'inzervera',
            title: translate('sections.reports.pitcher.inz-erv-era'),
            statistic: 'IZERVERA',
            precision: 2,
        },
        {
            id: 'rvera',
            title: translate('sections.reports.pitcher.rv-era'),
            statistic: 'rvERA',
            precision: 2,
        },
        {
            id: 'ervera',
            title: translate('sections.reports.pitcher.erv-era'),
            statistic: 'eRVERA',
            headerClass: 'border-end',
            cellClass: 'border-end',
            precision: 2,
        },
        {
            id: 'pitchfxera',
            title: translate('sections.reports.pitcher.pitchfx-era'),
            statistic: 'PitchFXERA',
            precision: 2,
        },
        {
            id: 'breakera',
            title: translate('sections.reports.pitcher.break-era'),
            statistic: 'BreakERA',
            precision: 2,
        },
        {
            id: 'stuffera',
            title: translate('sections.reports.pitcher.stuff-era'),
            statistic: 'StuffERA',
            precision: 2,
        },
        {
            id: 'execera',
            title: translate('sections.reports.pitcher.exec-era'),
            statistic: 'ExecERA',
            headerClass: 'border-end',
            cellClass: 'border-end',
            precision: 2,
        },
        {
            id: 'breakx54',
            title: translate('sections.reports.pitcher.pitchcomparison.horbreak'),
            statistic: 'BreakX',
            precision: 1,
            transform: (r) => (r.pitchtype < 0 ? '--' : r.breakx54),
        },
        {
            id: 'breakz54',
            title: translate('sections.reports.pitcher.pitchcomparison.vertbreak'),
            statistic: 'BreakZ',
            precision: 1,
            transform: (r) => (r.pitchtype < 0 ? '--' : r.breakz54),
        },
        {
            id: 'spinrate',
            title: translate('sections.reports.pitcher.spin-rate'),
            statistic: 'SpinRate',
            transform: (r) => (r.pitchtype < 0 ? '--' : r.spinrate),
        },
        {
            id: 'gyrodegree',
            title: translate('sections.reports.pitcher.gyro-degree'),
            statistic: 'GyroDeg',
            transform: (r) => (r.pitchtype < 0 ? '--' : r.gyrodegree),
        },
        {
            id: 'initposx',
            title: translate('data.release-x'),
            statistic: 'ReleaseX',
            precision: 1,
            transform: (r) => (r.pitchtype < 0 ? '--' : r.initposx),
        },
        {
            id: 'initposz',
            title: translate('data.release-z'),
            statistic: 'ReleaseZ',
            precision: 1,
            transform: (r) => (r.pitchtype < 0 ? '--' : r.initposz),
        },
        {
            id: 'armangle',
            title: translate('data.arm-angle'),
            statistic: 'ArmAngle',
            precision: 1,
            headerClass: 'border-end',
            cellClass: 'border-end',
            transform: (r) => (r.pitchtype < 0 ? '--' : r.armangle),
        },
        {
            id: 'xk100',
            title: translate('sections.reports.pitcher.pitchcomparison.xk100'),
            statistic: 'xk100',
            transform: (r) => extraStrikesPer100(r.expstk, r.tkstk, r.tk),
            precision: 1,
        },
        {
            id: 'kzone',
            title: translate('sections.reports.pitcher.kzon-pct'),
            statistic: 'KZon',
            type: columnTypes.PERCENT,
            transform: (r) => ratio(r.expstk_all, r.exppit_all),
        },
        {
            id: 'ch',
            title: translate('sections.reports.pitcher.chase'),
            statistic: 'Ch',
            transform: (r) => ratio(r.ch, r.outz),
            type: columnTypes.PERCENT,
        },
        {
            id: 'swdec',
            title: translate('baseball.abbr-swingdecision'),
            statistic: 'Swds',
            transform: (r) => ratio(r.swds, r.swdspit),
            precision: 1,
        },
        {
            id: 'stk',
            title: translate('sections.reports.pitcher.abbr-strike'),
            statistic: 'Stk',
            type: columnTypes.PERCENT,
            transform: (r) => ratio(r.stk, r.pitches),
            headerClass: 'border-end',
            cellClass: 'border-end',
        },
        {
            id: 'ms',
            title: translate('sections.reports.pitcher.abbr-miss'),
            statistic: 'Ms',
            type: columnTypes.PERCENT,
            transform: (r) => ratio(r.ms, r.sw),
        },
        {
            id: 'kopp',
            title: translate('sections.reports.pitcher.put-away-pct'),
            statistic: 'KOpp',
            type: columnTypes.PERCENT,
            transform: (r) => ratio(r.so, r.kopp),
            headerClass: 'border-end',
            cellClass: 'border-end',
        },
        {
            id: 'gb',
            title: translate('baseball.abbr-groundball-pct'),
            statistic: 'GBPercent',
            type: columnTypes.PERCENT,
            transform: (r) => ratio(r.gb, r.bip),
        },
        {
            id: 'cr',
            title: translate('baseball.abbr-contactresults'),
            statistic: 'ContactResults',
            transform: (r) => (r.biprvdenom > 0 ? rvPer100BIP(r.rvhitfxactualbip, r.biprvdenom) : null),
            precision: 1,
        },
        {
            id: 'cq',
            title: translate('baseball.abbr-contactquality'),
            statistic: 'ContactQuality',
            transform: (r) => (r.biprvdenom > 0 ? rvPer100BIP(r.rvhitfxpitbip, r.biprvdenom) : null),
            precision: 1,
        },
        {
            id: 'slg',
            title: translate('baseball.abbr-slugging'),
            statistic: 'SLG',
            type: columnTypes.RATE,
            transform: ({ b1, b2, b3, hr, ab }) => slg(b1, b2, b3, hr, ab),
        },
    ].map((c) => ({
        ...c,
        colSpan: c.colSpan || ((r) => (r?.isLoadingRow && isLoadingAvg ? 0 : 1)),
    }));

    const allRows = [
        ...pitchTypes,
        ...(isLoadingAvg ? [{ isLoadingRow: true, pitchtype: -2 }] : []),
    ];

    const rowClass = (r) => {
        const rowClasses = [];
        if (r.pitchtype === -1 || r.pitchtype === -2) rowClasses.push('fw-bold');
        if (r.pitchtype === -1) rowClasses.push('border-top border-secondary');

        return rowClasses.join(' ');
    };

    return (
        <div className="text-center">
            <DataTable
                statTooltip={StatTooltip}
                superheader={SuperHeader}
                columns={columns}
                rows={allRows}
                rowClass={rowClass}
            />
        </div>
    );
};
export default PitchTabTable;
